import { Component, OnInit, OnDestroy, ViewChild, ElementRef, EventEmitter, Input, Output } from '@angular/core';
import { VendorService } from '../../../services/vendor.service';
import { PrintService } from '../../../services/print.service';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { VendorPaymentModalComponent } from '../../vendor-payment-modal/vendor-payment-modal.component';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { Subject, forkJoin, merge, of } from 'rxjs';
import { catchError, map, take, takeUntil } from 'rxjs/operators';
import { buildDashboardThemeVars } from '../../../shared/dashboard-widget-theme';

@Component({
	selector: 'app-vendordash-brand-disbursements',
	templateUrl: './vendordash-brand-disbursements.component.html',
	styleUrls: ['./vendordash-brand-disbursements.component.scss'],
	animations: [
		trigger('flipState', [
			state('active', style({ transform: 'rotate3d(0, 1, 0, 179deg)' })),
			state('inactive', style({ transform: 'rotateY(0)' })),
			transition('active => inactive', animate('500ms ease-out')),
			transition('inactive => active', animate('500ms ease-in')),
		]),
	]
})
export class VendordashBrandDisbursementsComponent implements OnInit, OnDestroy {

	flip = 'inactive';
	@Output() viewData = new EventEmitter<any>();
	/** Open the table view as soon as rows land instead of waiting for a click.
	 *  Brand payouts are the reason MOOV opens this screen, so on that tenant the
	 *  card is the landing view rather than one of eleven tiles to hunt for. */
	@Input() defaultOpen = false;
	private autoOpened = false;

	rows: any[] = [];
	rowsLoaded = false;
	viewing: any = false;
	title = '';
	exporting = false;
	@ViewChild('print_table') printtable: ElementRef;

	searchTerm = '';
	filterMode: 'all' | 'ready' = 'all';
	sortField = 'net_payout_to_brand';
	sortDir: 'asc' | 'desc' = 'desc';
	expandedRow: any = null;
	detailRows: any[] = [];
	detailLoading = false;
	paymentLinks: any[] = [];
	paymentLinksLoading = false;
	includeSettledPaymentLinks = false;

	private readonly destroy$ = new Subject<void>();
	color = 'blue';

	get themeVars(): Record<string, string> {
		return buildDashboardThemeVars('cw', this.color);
	}

	get cardTotalDisplay(): string | null {
		if (!this.rowsLoaded) {
			return null;
		}
		// Cents, not whole dollars: this figure has to tie to the Payable total in
		// the table footer, and a rounded card read as the payout not adding up.
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(this.cardTotal);
	}

	constructor(
		private vendorService: VendorService,
		private printService: PrintService,
		private globalSearchService: GlobalSearchService,
		private modalService: NgbModal,
	) {}

	ngOnInit(): void {
		this.color = this.globalSearchService.getColor();
		this.loadDashboard();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private loadDashboard(): void {
		this.vendorService.getDashBoard().subscribe((results: any) => {
			this.rows = results?.brandDisbursements || [];
			this.rowsLoaded = true;
			if (this.defaultOpen && !this.autoOpened && this.rows.length) {
				this.autoOpened = true;
				this.toggleFlip(this.rows, 'All');
			}
			if (this.viewing) {
				this.viewing = this.rows;
			}
			// Drop expanded row if the brand fell off the list (e.g. net payout → 0).
			if (this.expandedRow) {
				const stillThere = (this.rows || []).some((r: any) =>
					String(r.supplierid) === String(this.expandedRow.supplierid));
				if (!stillThere) {
					this.expandedRow = null;
				} else {
					// Repoint to the fresh row object so currency / amounts update.
					this.expandedRow = (this.rows || []).find((r: any) =>
						String(r.supplierid) === String(this.expandedRow.supplierid)) || null;
				}
			}
		});
	}

	toggleFlip(data: any, title: string) {
		if (!data) {
			this.flip = 'inactive';
		} else {
			this.flip = (this.flip == 'active') ? 'inactive' : 'active';
		}
		if (this.flip == 'active') {
			this.viewing = data;
			this.title = title;
			this.expandedRow = null;
			this.viewData.emit({ data, title: 'Brand Payouts Pending' });
		} else {
			this.viewing = false;
			this.viewData.emit(false);
		}
	}

	openPaymentModal(row: any): void {
		if (this.toNum(row?.net_payout_to_brand) <= 0) {
			return;
		}
		const ref = this.modalService.open(VendorPaymentModalComponent, {
			size: 'xl',
			scrollable: true,
			windowClass: 'vendor-payment-modal-window',
		});
		ref.componentInstance.supplierid = String(row.supplierid);
		ref.componentInstance.vendorTitle = `${row.suppname} · ${row.supplierid}`;
		ref.componentInstance.payoutRow = row;

		merge(ref.closed, ref.dismissed)
			.pipe(take(1), takeUntil(this.destroy$))
			.subscribe(() => {
				this.loadDashboard();
			});
	}

	toggleDetails(row: any) {
		if (this.expandedRow === row) {
			this.expandedRow = null;
			this.detailRows = [];
			this.paymentLinks = [];
			this.includeSettledPaymentLinks = false;
			return;
		}
		this.expandedRow = row;
		this.detailRows = [];
		this.paymentLinks = [];
		this.includeSettledPaymentLinks = false;
		this.detailLoading = true;

		const today = new Date();
		const fiveYearsAgo = new Date();
		fiveYearsAgo.setFullYear(today.getFullYear() - 5);
		this.vendorService.getVendorTransactions({
			supplierid: row.supplierid,
			opentrans: true,
			types: [20, 21],
			fromdate: fiveYearsAgo,
			todate: today,
			keywords: '',
		}).subscribe((res: any) => {
			const list = res?.transactions ?? res ?? [];
			this.detailRows = Array.isArray(list) ? list : [];
			this.detailLoading = false;
		}, () => {
			this.detailLoading = false;
		});

		this.loadPaymentLinks(row);
	}

	loadPaymentLinks(row: any = this.expandedRow): void {
		if (!row) {
			return;
		}
		this.paymentLinksLoading = true;
		this.vendorService.getBrandPaymentLinks(row.supplierid, this.includeSettledPaymentLinks).subscribe((res: any) => {
			this.paymentLinks = res?.links ?? [];
			this.paymentLinksLoading = false;
		}, () => {
			this.paymentLinksLoading = false;
		});
	}

	toggleSettledPaymentLinks(event: MouseEvent): void {
		event.stopPropagation();
		this.includeSettledPaymentLinks = !this.includeSettledPaymentLinks;
		this.paymentLinks = [];
		this.loadPaymentLinks();
	}

	sortBy(field: string) {
		if (this.sortField === field) {
			this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
		} else {
			this.sortField = field;
			const numericFields = ['brand_ap_open', 'link_ready', 'fronted_uncollected', 'metrc_withhold', 'fees_held', 'net_payout_to_brand', 'ready_total'];
			this.sortDir = numericFields.includes(field) ? 'desc' : 'asc';
		}
		this.expandedRow = null;
	}

	getSortIcon(field: string): string {
		if (this.sortField !== field) {
			return 'unfold_more';
		}
		return this.sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward';
	}

	get displayRows(): any[] {
		let data: any[] = this.viewing || [];

		if (this.filterMode === 'ready') {
			data = data.filter(r => this.toNum(r.ready_total) > 0 || this.toNum(r.net_payout_to_brand) > 0);
		}

		const term = this.searchTerm.trim().toLowerCase();
		if (term) {
			data = data.filter(r =>
				(r.suppname || '').toLowerCase().includes(term) ||
				String(r.supplierid || '').toLowerCase().includes(term)
			);
		}

		const field = this.sortField;
		const dir = this.sortDir;
		const numericFields = ['brand_ap_open', 'link_ready', 'fronted_uncollected', 'metrc_withhold', 'fees_held', 'net_payout_to_brand', 'ready_total'];
		data = [...data].sort((a, b) => {
			let av: any = a[field];
			let bv: any = b[field];
			if (field === 'link_ready') {
				av = this.availableNet(a);
				bv = this.availableNet(b);
			} else if (numericFields.includes(field)) {
				av = this.toNum(av);
				bv = this.toNum(bv);
			} else {
				av = (av || '').toString().toLowerCase();
				bv = (bv || '').toString().toLowerCase();
			}
			if (av < bv) {
				return dir === 'asc' ? -1 : 1;
			}
			if (av > bv) {
				return dir === 'asc' ? 1 : -1;
			}
			return 0;
		});

		return data;
	}

	/** Signed Available. The server floors link_ready at 0 because the payment
	 *  screen prefills from it, but the row on screen reads
	 *  "Available - Hold = Payout" — so an over-paid brand (collected less than
	 *  already disbursed) printed a floored $0 that did not tie to its own
	 *  payout. Derived from the payout itself, so the row always adds up. */
	availableNet(row: any): number {
		return this.round2(this.toNum(row?.net_payout_to_brand) + this.toNum(row?.fronted_uncollected));
	}

	/** Sum of the Payout column exactly as displayed — negatives included. */
	get displayTotal(): number {
		return this.round2((this.displayRows || []).reduce(
			(acc, r) => acc + this.toNum(r.net_payout_to_brand), 0));
	}

	/** What MOOV actually owes brands: positive balances only. Ties to the card. */
	get displayPayableTotal(): number {
		return this.round2(this.sumPositiveNetPayout(this.displayRows));
	}

	get displayAvailableTotal(): number {
		return this.round2((this.displayRows || []).reduce(
			(acc, r) => acc + this.availableNet(r), 0));
	}

	get displayHoldTotal(): number {
		return this.round2((this.displayRows || []).reduce(
			(acc, r) => acc + this.toNum(r.fronted_uncollected), 0));
	}

	get payableCount(): number {
		return (this.displayRows || []).filter(r => this.toNum(r.net_payout_to_brand) > 0).length;
	}

	private round2(v: number): number {
		return Math.round((v + Number.EPSILON) * 100) / 100;
	}

	get cardCount(): number {
		return (this.rows || []).filter(r => this.toNum(r.ready_total) > 0).length;
	}

	get cardTotal(): number {
		return this.sumPositiveNetPayout(this.rows);
	}

	private sumPositiveNetPayout(rows: any[]): number {
		// Only amounts the company owes brands (positive net payouts); negative rows are excluded.
		return (rows || []).reduce((acc, r) => {
			const n = this.toNum(r.net_payout_to_brand);
			return n > 0 ? acc + n : acc;
		}, 0);
	}

	// A row has live fee columns only if it was created by the post-May-11 trigger
	// (suppreference starts with 'BRAND-'). Legacy BC-IMPORT rows have historical
	// pp_* values but the underlying fee was already paid to V00043 — showing
	// them would imply a current obligation that doesn't exist.
	isFeeBearing(inv: any): boolean {
		const ref = String(inv?.suppreference || '');
		return ref.indexOf('BRAND-') === 0;
	}

	customerPaidAmount(lnk: any): number {
		return this.toNum(lnk?.customer_paid_amount);
	}

	isCustomerPaid(lnk: any): boolean {
		return !!lnk?.customer_paid_at || this.customerPaidAmount(lnk) > 0.005;
	}

	customerPaidLabel(lnk: any): string {
		if (lnk?.customer_paid_at) {
			return lnk.customer_paid_at;
		}
		const paid = this.customerPaidAmount(lnk);
		const gross = this.toNum(lnk?.gross_amount || lnk?.inv_amount);
		if (gross > 0 && paid + 0.005 < gross) {
			return 'Partial';
		}
		return 'Paid';
	}

	customerPaidTitle(lnk: any): string {
		const paid = this.customerPaidAmount(lnk);
		const gross = this.toNum(lnk?.gross_amount || lnk?.inv_amount);
		const amount = paid > 0 ? `${paid.toFixed(2)} paid` : '';
		if (lnk?.customer_paid_at && amount) {
			return `${lnk.customer_paid_at} - ${amount}`;
		}
		if (amount && gross > 0) {
			return `${amount} of ${gross.toFixed(2)}`;
		}
		return lnk?.customer_paid_at || '';
	}

	toNum(v: any): number {
		if (v == null || v === '') {
			return 0;
		}
		if (typeof v === 'number') {
			return v;
		}
		const cleaned = String(v).replace(/[^\d.-]/g, '');
		const parsed = parseFloat(cleaned);
		return isNaN(parsed) ? 0 : parsed;
	}

	exportPdf() {
		this.exporting = true;
		this.buildAuditExportHtml().subscribe((html: string) => {
			const encoded: string = this.globalSearchService.base64encode(html);
			const today = new Date();
			const data = {
				content: encoded,
				filename: 'brand-payouts-pending-audit.pdf',
				landscape: true,
				title: 'Brand Payouts Pending Audit',
				subtitle: 'Created ' + today.toLocaleString()
			};
			this.printService.pdf(data).subscribe((result: any) => {
				this.globalSearchService.downloadPdf(result.content, data.filename);
				this.exporting = false;
			}, () => {
				this.exporting = false;
			});
		}, () => {
			this.exporting = false;
		});
	}

	exportXls() {
		this.exporting = true;
		this.buildAuditExportHtml().subscribe((html: string) => {
			const encoded: string = this.globalSearchService.base64encode(html);
			const today = new Date();
			const data = {
				content: encoded,
				filename: 'brand-payouts-pending-audit.xlsx',
				title: 'Brand Payouts Pending Audit',
				subtitle: 'Created ' + today.toLocaleString()
			};
			this.printService.xls(data).subscribe((result: any) => {
				this.globalSearchService.downloadXls(result.content, data.filename);
				this.exporting = false;
			}, () => {
				this.exporting = false;
			});
		}, () => {
			this.exporting = false;
		});
	}

	private buildAuditExportHtml() {
		const rows = this.displayRows || [];
		if (!rows.length) {
			return of('<div>No brand payout rows to export.</div>');
		}
		const requests = rows.map((row: any) =>
			this.vendorService.getBrandPaymentLinks(row.supplierid, true).pipe(
				map((res: any) => ({ row, links: res?.links || [] })),
				catchError(() => of({ row, links: [] })),
			)
		);
		return forkJoin(requests).pipe(map((items: any[]) => this.auditExportHtml(items)));
	}

	private auditExportHtml(items: Array<{ row: any; links: any[] }>): string {
		const today = new Date().toLocaleString();
		const summaryRows = items.map(({ row }) => `
			<tr>
				<td>${this.escapeHtml(row.supplierid)}</td>
				<td>${this.escapeHtml(row.suppname)}</td>
				<td class="num">${this.money(row.brand_ap_open)}</td>
				<td class="num">${this.money(row.metrc_withhold)}</td>
				<td class="num">${this.money(row.fees_held)}</td>
				<td class="num">${this.money(this.availableNet(row))}</td>
				<td class="num">${this.money(row.fronted_uncollected)}</td>
				<td class="num">${this.money(row.net_payout_to_brand)}</td>
				<td>${this.toNum(row.net_payout_to_brand) < 0 ? 'Negative balance' : 'Payable'}</td>
			</tr>`).join('');
		const detailRows = items.map(({ row, links }) => {
			const body = (links || []).map((lnk: any) => `
				<tr>
					<td>${this.escapeHtml(row.supplierid)}</td>
					<td>${this.escapeHtml(row.suppname)}</td>
					<td>${this.escapeHtml(lnk.customer_name || lnk.debtorno || '')}</td>
					<td>${this.escapeHtml(lnk.inv_transno || '')}</td>
					<td>${this.escapeHtml(lnk.inv_trandate || '')}</td>
					<td>${this.escapeHtml(lnk.inv_reference || '')}</td>
					<td class="num">${this.money(lnk.inv_amount)}</td>
					<td class="num">${this.money(lnk.brand_amount)}</td>
					<td class="num">${this.money(lnk.excise_held)}</td>
					<td class="num">${this.money(lnk.fees_held)}</td>
					<td>${this.isCustomerPaid(lnk) ? 'Collected' : 'Pending'}</td>
					<td class="num">${this.money(lnk.customer_paid_amount)}</td>
					<td>${this.escapeHtml(lnk.customer_paid_at || '')}</td>
					<td>${this.escapeHtml(lnk.pay_transno ? '#' + lnk.pay_transno : '')}</td>
					<td>${lnk.brand_ap_settled == '1' ? 'Settled' : 'Open'}</td>
				</tr>`).join('');
			return body || `
				<tr>
					<td>${this.escapeHtml(row.supplierid)}</td>
					<td>${this.escapeHtml(row.suppname)}</td>
					<td colspan="13">No customer payment source rows found.</td>
				</tr>`;
		}).join('');
		return `
			<style>
				table{border-collapse:collapse;width:100%;font-family:Arial,sans-serif;font-size:11px}
				th,td{border:1px solid #ccc;padding:4px 6px;vertical-align:top}
				th{background:#f2f2f2;font-weight:700}
				.num{text-align:right;mso-number-format:"\\$#,##0.00;[Red]-\\$#,##0.00"}
				h2{font-family:Arial,sans-serif;margin:16px 0 6px}
				.meta{font-family:Arial,sans-serif;color:#666;margin-bottom:10px}
			</style>
			<h2>Brand Payouts Pending Audit</h2>
			<div class="meta">Created ${this.escapeHtml(today)}. Detail includes settled and open customer payment source rows.</div>
			<h2>Summary</h2>
			<table>
				<thead>
					<tr>
						<th>Brand ID</th><th>Brand</th><th>Open AP (ref)</th><th>Excise to NYS (ref)</th>
						<th>Fees Earned (ref)</th><th>Available</th><th>Less Payout Hold</th><th>Final Payout</th><th>Status</th>
					</tr>
				</thead>
				<tbody>${summaryRows}</tbody>
				<tfoot>
					<tr>
						<th colspan="5">Total &mdash; ${items.length} brand${items.length === 1 ? '' : 's'}</th>
						<th class="num">${this.money(this.displayAvailableTotal)}</th>
						<th class="num">${this.money(this.displayHoldTotal)}</th>
						<th class="num">${this.money(this.displayTotal)}</th>
						<th>Net position</th>
					</tr>
					<tr>
						<th colspan="7">Payable now &mdash; positive balances only (${this.payableCount})</th>
						<th class="num">${this.money(this.displayPayableTotal)}</th>
						<th>Payable</th>
					</tr>
				</tfoot>
			</table>
			<h2>Customer Payment Sources</h2>
			<table>
				<thead>
					<tr>
						<th>Brand ID</th><th>Brand</th><th>Customer</th><th>Inv #</th><th>Inv Date</th>
						<th>Order Ref</th><th>Customer Inv</th><th>Brand Portion</th><th>Excise</th>
						<th>Fees</th><th>Customer Status</th><th>Customer Paid Amount</th><th>Customer Paid Date</th>
						<th>Payment Ref</th><th>AP Settled</th>
					</tr>
				</thead>
				<tbody>${detailRows}</tbody>
			</table>`;
	}

	private money(v: any): string {
		return this.toNum(v).toFixed(2);
	}

	private escapeHtml(v: any): string {
		return String(v == null ? '' : v)
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;');
	}
}
